import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Megaphone } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const LEVELS = ["INFO", "SUCCESS", "WARNING", "CRITICAL"];

const LEVEL_TONE = {
  INFO: "bg-blue-50 text-blue-700 border-blue-200",
  SUCCESS: "bg-emerald-50 text-emerald-700 border-emerald-200",
  WARNING: "bg-amber-50 text-amber-700 border-amber-200",
  CRITICAL: "bg-rose-50 text-rose-700 border-rose-200",
};

const blank = {
  title: "", body: "", level: "INFO", audience_type: "ALL", audience_value: null,
  is_published: false, is_dismissible: true, starts_at: null, ends_at: null,
};

// <input type="date"> needs "" not null, and the API wants ISO or null.
const toDateInput = (v) => (v ? String(v).slice(0, 10) : "");
const fromDateInput = (v) => (v ? new Date(`${v}T00:00:00Z`).toISOString() : null);

export default function Announcements() {
  const [rows, setRows] = useState([]);
  const [plans, setPlans] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);

  const load = () => api.get("/platform/announcements").then(({ data }) => setRows(data));

  useEffect(() => {
    load();
    api.get("/platform/plans").then(({ data }) => setPlans(data));
    api.get("/platform/organizations").then(({ data }) => setOrgs(data));
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm(blank);
    setOpen(true);
  };

  const openEdit = (a) => {
    setEditing(a);
    setForm({ ...blank, ...a });
    setOpen(true);
  };

  const save = async () => {
    const payload = {
      ...form,
      starts_at: form.starts_at || null,
      ends_at: form.ends_at || null,
      audience_value: form.audience_type === "ALL" ? null : form.audience_value,
    };
    try {
      if (editing) await api.patch(`/platform/announcements/${editing.id}`, payload);
      else await api.post("/platform/announcements", payload);
      toast.success(editing ? "Announcement updated" : "Announcement created");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.title}"?`)) return;
    try {
      await api.delete(`/platform/announcements/${a.id}`);
      toast.success("Announcement deleted");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const togglePublish = async (a) => {
    try {
      await api.patch(`/platform/announcements/${a.id}`, { ...a, is_published: !a.is_published });
      toast.success(a.is_published ? "Unpublished" : "Published to tenants");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const audienceLabel = (a) => {
    if (a.audience_type === "ALL") return "All organisations";
    if (a.audience_type === "PLAN") return `Plan: ${a.audience_value}`;
    return `Org: ${orgs.find((o) => o.id === a.audience_value)?.name || a.audience_value}`;
  };

  return (
    <div className="space-y-6" data-testid="platform-announcements-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Announcements</h1>
          <p className="mt-1 text-sm text-slate-500">
            Banners shown inside the tenant app — to everyone, one plan, or one organisation
          </p>
        </div>
        <Button onClick={openNew} data-testid="new-announcement-btn" className="gap-1.5 rounded-full bg-violet-600 hover:bg-violet-700">
          <Plus size={16} /> New Announcement
        </Button>
      </div>

      <div className="space-y-3">
        {rows.map((a) => (
          <div
            key={a.id}
            className="rounded-lg border border-slate-200 bg-white p-4"
            data-testid={`announcement-${a.id}`}
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={`rounded border px-1.5 py-0.5 text-[10px] font-semibold uppercase ${LEVEL_TONE[a.level]}`}
                  >
                    {a.level}
                  </span>
                  <span className="font-heading text-base font-bold">{a.title}</span>
                  {a.is_published ? (
                    <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] uppercase text-emerald-700">
                      Live
                    </span>
                  ) : (
                    <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] uppercase text-slate-500">
                      Draft
                    </span>
                  )}
                </div>
                <p className="mt-1.5 whitespace-pre-wrap text-sm text-slate-600">{a.body}</p>
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-500">
                  <span>{audienceLabel(a)}</span>
                  <span>
                    {a.starts_at ? `From ${fmtDate(a.starts_at)}` : "No start date"}
                    {" · "}
                    {a.ends_at ? `until ${fmtDate(a.ends_at)}` : "no end date"}
                  </span>
                  <span>{a.dismiss_count} dismissed</span>
                  {!a.is_dismissible && <span>Not dismissible</span>}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-3">
                <button
                  onClick={() => togglePublish(a)}
                  data-testid={`announcement-publish-${a.id}`}
                  className="text-xs font-medium text-violet-700 transition-colors duration-150 hover:text-violet-900"
                >
                  {a.is_published ? "Unpublish" : "Publish"}
                </button>
                <button
                  onClick={() => openEdit(a)}
                  data-testid={`announcement-edit-${a.id}`}
                  className="text-xs font-medium text-slate-600 transition-colors duration-150 hover:text-slate-900"
                >
                  Edit
                </button>
                <button
                  onClick={() => remove(a)}
                  data-testid={`announcement-delete-${a.id}`}
                  className="text-slate-400 transition-colors duration-150 hover:text-rose-600"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          </div>
        ))}
        {!rows.length && (
          <div className="rounded-lg border border-dashed border-slate-300 bg-white py-16 text-center">
            <Megaphone size={22} className="mx-auto text-slate-300" />
            <p className="mt-2 text-sm text-slate-500">No announcements yet</p>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto" data-testid="announcement-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit announcement" : "New announcement"}</DialogTitle>
          </DialogHeader>

          <div className="grid gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Title</Label>
              <Input
                data-testid="announcement-title-input"
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="Scheduled maintenance on Sunday"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Message</Label>
              <Textarea
                data-testid="announcement-body-input"
                rows={4}
                value={form.body}
                onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
              />
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Level</Label>
                <Select value={form.level} onValueChange={(v) => setForm((f) => ({ ...f, level: v }))}>
                  <SelectTrigger data-testid="announcement-level-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {LEVELS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Audience</Label>
                <Select
                  value={form.audience_type}
                  onValueChange={(v) => setForm((f) => ({ ...f, audience_type: v, audience_value: null }))}
                >
                  <SelectTrigger data-testid="announcement-audience-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">All organisations</SelectItem>
                    <SelectItem value="PLAN">A specific plan</SelectItem>
                    <SelectItem value="ORG">A specific organisation</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {form.audience_type === "PLAN" && (
              <div className="space-y-1.5">
                <Label className="text-xs">Plan</Label>
                <Select
                  value={form.audience_value || ""}
                  onValueChange={(v) => setForm((f) => ({ ...f, audience_value: v }))}
                >
                  <SelectTrigger data-testid="announcement-plan-select">
                    <SelectValue placeholder="Choose a plan" />
                  </SelectTrigger>
                  <SelectContent>
                    {plans.map((p) => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            {form.audience_type === "ORG" && (
              <div className="space-y-1.5">
                <Label className="text-xs">Organisation</Label>
                <Select
                  value={form.audience_value || ""}
                  onValueChange={(v) => setForm((f) => ({ ...f, audience_value: v }))}
                >
                  <SelectTrigger data-testid="announcement-org-select">
                    <SelectValue placeholder="Choose an organisation" />
                  </SelectTrigger>
                  <SelectContent>
                    {orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Show from</Label>
                <Input
                  type="date"
                  data-testid="announcement-start-input"
                  value={toDateInput(form.starts_at)}
                  onChange={(e) => setForm((f) => ({ ...f, starts_at: fromDateInput(e.target.value) }))}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Show until</Label>
                <Input
                  type="date"
                  data-testid="announcement-end-input"
                  value={toDateInput(form.ends_at)}
                  onChange={(e) => setForm((f) => ({ ...f, ends_at: fromDateInput(e.target.value) }))}
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-6 pt-1">
              <label className="flex items-center gap-2 text-sm">
                <Switch
                  checked={form.is_published}
                  data-testid="announcement-published-switch"
                  onCheckedChange={(v) => setForm((f) => ({ ...f, is_published: v }))}
                />
                Publish now
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Switch
                  checked={form.is_dismissible}
                  data-testid="announcement-dismissible-switch"
                  onCheckedChange={(v) => setForm((f) => ({ ...f, is_dismissible: v }))}
                />
                Tenants can dismiss
              </label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="announcement-save" className="bg-violet-600 hover:bg-violet-700">
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
